import React, { useState, useRef } from "react";
import API from "../../services/api.ts";
import { toast } from "react-toastify";

const DatasetFolderUpload = ({ onPatientsCreated }) => {
    const [files, setFiles] = useState([]);
    const [relativePaths, setRelativePaths] = useState([]);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [description, setDescription] = useState("");
    const [isAnonymized, setIsAnonymized] = useState(false);
    const [isSynthetic, setIsSynthetic] = useState(false);
    const fileInputRef = useRef(null);

    const handleFolderSelect = async (e) => {
        const selectedFiles = Array.from(e.target.files);

        if (selectedFiles.length === 0) {
            return;
        }

        // Extract relative paths from the webkitRelativePath
        const paths = selectedFiles.map(file => file.webkitRelativePath);

        setFiles(selectedFiles);
        setRelativePaths(paths);
        setPreview(null);

        // Send to backend for parsing
        setLoading(true);
        try {
            const formData = new FormData();
            selectedFiles.forEach((file, index) => {
                formData.append('files', file);
                formData.append('relative_paths', paths[index]);
            });

            const response = await API.post('/data_manager/dataset-upload/parse/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setPreview(response.data);
        } catch (err) {
            console.error("Error parsing dataset:", err);
            toast.error("Failed to parse dataset folder.");
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmUpload = async () => {
        if (!preview || preview.total_patients === 0) {
            toast.warning("No patients to upload.");
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            files.forEach((file, index) => {
                formData.append('files', file);
                formData.append('relative_paths', relativePaths[index]);
            });
            formData.append('description', description);
            formData.append('is_anonymized', isAnonymized);
            formData.append('is_synthetic', isSynthetic);

            const response = await API.post('/data_manager/dataset-upload/confirm/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            toast.success(`Successfully created ${response.data.total_patients} patients with ${response.data.total_files} data items!`);

            // Call callback with created patients
            if (onPatientsCreated) {
                onPatientsCreated(response.data.patients);
            }

            // Reset state
            setFiles([]);
            setRelativePaths([]);
            setPreview(null);
            setDescription("");
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        } catch (err) {
            console.error("Error uploading dataset:", err);
            toast.error("Failed to upload dataset.");
        } finally {
            setUploading(false);
        }
    };

    const handleCancel = () => {
        setFiles([]);
        setRelativePaths([]);
        setPreview(null);
        setDescription("");
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const getDataTypeIcon = (dataType) => {
        const icons = {
            imaging: 'fa-x-ray',
            image: 'fa-image',
            document: 'fa-file-text',
            table: 'fa-table',
            signal: 'fa-wave-square'
        };
        return icons[dataType] || 'fa-file';
    };

    const getDataTypeBadgeColor = (dataType) => {
        const colors = {
            imaging: 'primary',
            image: 'success',
            document: 'warning',
            table: 'info',
            signal: 'secondary'
        };
        return colors[dataType] || 'dark';
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    return (
        <div className="dataset-folder-upload">
            {/* Folder Selection */}
            {!preview && (
                <div className="folder-select-area">
                    <div className="folder-dropzone">
                        <input
                            ref={fileInputRef}
                            type="file"
                            webkitdirectory=""
                            directory=""
                            multiple
                            onChange={handleFolderSelect}
                            className="folder-input"
                            id="folder-input"
                        />
                        <label htmlFor="folder-input" className="folder-label">
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Parsing folder structure...
                                </>
                            ) : (
                                <>
                                    <i className="fa fa-folder-open fa-3x mb-3"></i>
                                    <h5>Select Dataset Folder</h5>
                                    <p className="text-muted">
                                        Choose a folder containing patient subfolders.
                                        <br />
                                        Each subfolder name will be used as the Patient ID.
                                    </p>
                                    <span className="btn btn-outline-primary mt-2">
                                        <i className="fa fa-folder me-2"></i>
                                        Browse Folder
                                    </span>
                                </>
                            )}
                        </label>
                    </div>
                    <div className="folder-structure-hint">
                        <h6><i className="fa fa-info-circle me-2"></i>Expected Folder Structure:</h6>
                        <pre className="structure-example">
{`dataset_folder/
├── Patient_001/
│   ├── image1.png
│   ├── scan.dcm
│   └── report.pdf
├── Patient_002/
│   ├── image1.jpg
│   └── data.csv
└── Patient_003/
    └── notes.md`}
                        </pre>
                    </div>
                </div>
            )}

            {/* Preview Section */}
            {preview && (
                <div className="dataset-preview">
                    <div className="preview-header">
                        <h5>
                            <i className="fa fa-check-circle text-success me-2"></i>
                            Dataset Preview
                        </h5>
                        <div className="preview-stats">
                            <span className="stat-badge">
                                <i className="fa fa-users me-1"></i>
                                {preview.total_patients} Patients
                            </span>
                            <span className="stat-badge">
                                <i className="fa fa-file me-1"></i>
                                {preview.total_files} Files
                            </span>
                        </div>
                    </div>

                    {/* Options */}
                    <div className="upload-options mb-3">
                        <div className="row">
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label className="form-label">Description (optional)</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter description for all data items"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-check mt-4">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="is-anonymized"
                                        checked={isAnonymized}
                                        onChange={(e) => setIsAnonymized(e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="is-anonymized">
                                        Anonymized Data
                                    </label>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-check mt-4">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="is-synthetic"
                                        checked={isSynthetic}
                                        onChange={(e) => setIsSynthetic(e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="is-synthetic">
                                        Synthetic Data
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Patients List */}
                    <div className="patients-preview-list">
                        {preview.patients.map((patient, index) => (
                            <div key={index} className="patient-preview-card">
                                <div className="patient-preview-header">
                                    <div className="patient-id">
                                        <i className="fa fa-user me-2"></i>
                                        <strong>{patient.patient_id}</strong>
                                    </div>
                                    <span className="file-count-badge">
                                        {patient.total_files} files
                                    </span>
                                </div>
                                <div className="patient-preview-data">
                                    {Object.entries(patient.data).map(([dataType, items]) => {
                                        if (items.length === 0) return null;
                                        return (
                                            <div key={dataType} className="data-type-group">
                                                <span className={`badge bg-${getDataTypeBadgeColor(dataType)} me-2`}>
                                                    <i className={`fa ${getDataTypeIcon(dataType)} me-1`}></i>
                                                    {dataType}: {items.length}
                                                </span>
                                                <div className="file-list">
                                                    {items.slice(0, 3).map((item, idx) => (
                                                        <span key={idx} className="file-item" title={item.filename}>
                                                            {item.filename}
                                                            <small className="text-muted ms-1">
                                                                ({formatFileSize(item.size)})
                                                            </small>
                                                        </span>
                                                    ))}
                                                    {items.length > 3 && (
                                                        <span className="text-muted">
                                                            +{items.length - 3} more
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Action Buttons */}
                    <div className="preview-actions">
                        <button
                            type="button"
                            className="btn btn-outline-secondary me-2"
                            onClick={handleCancel}
                            disabled={uploading}
                        >
                            <i className="fa fa-times me-2"></i>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-success"
                            onClick={handleConfirmUpload}
                            disabled={uploading}
                        >
                            {uploading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Creating Patients...
                                </>
                            ) : (
                                <>
                                    <i className="fa fa-check me-2"></i>
                                    Confirm & Create {preview.total_patients} Patients
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            <style>{`
                .dataset-folder-upload {
                    padding: 1rem 0;
                }

                .folder-dropzone {
                    border: 2px dashed rgba(255, 255, 255, 0.3);
                    border-radius: 12px;
                    padding: 2rem;
                    text-align: center;
                    background: rgba(0, 0, 0, 0.2);
                    transition: all 0.3s ease;
                }

                .folder-dropzone:hover {
                    border-color: rgba(59, 130, 246, 0.5);
                    background: rgba(59, 130, 246, 0.1);
                }

                .folder-input {
                    display: none;
                }

                .folder-label {
                    cursor: pointer;
                    display: block;
                    color: rgba(255, 255, 255, 0.8);
                }

                .folder-structure-hint {
                    margin-top: 1.5rem;
                    padding: 1rem;
                    background: rgba(0, 0, 0, 0.2);
                    border-radius: 8px;
                }

                .folder-structure-hint h6 {
                    color: rgba(255, 255, 255, 0.7);
                    margin-bottom: 0.5rem;
                }

                .structure-example {
                    background: rgba(0, 0, 0, 0.3);
                    padding: 1rem;
                    border-radius: 6px;
                    color: rgba(255, 255, 255, 0.6);
                    font-size: 0.85rem;
                    margin-bottom: 0;
                }

                .preview-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1rem;
                    padding-bottom: 1rem;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                }

                .preview-header h5 {
                    margin: 0;
                    color: #fff;
                }

                .preview-stats {
                    display: flex;
                    gap: 1rem;
                }

                .stat-badge {
                    background: rgba(59, 130, 246, 0.2);
                    color: #60a5fa;
                    padding: 0.4rem 0.8rem;
                    border-radius: 20px;
                    font-size: 0.85rem;
                }

                .patients-preview-list {
                    max-height: 400px;
                    overflow-y: auto;
                    margin-bottom: 1rem;
                }

                .patient-preview-card {
                    background: rgba(255, 255, 255, 0.05);
                    border-radius: 8px;
                    padding: 1rem;
                    margin-bottom: 0.75rem;
                }

                .patient-preview-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 0.75rem;
                }

                .patient-id {
                    color: #fff;
                    font-size: 1rem;
                }

                .file-count-badge {
                    background: rgba(255, 255, 255, 0.1);
                    color: rgba(255, 255, 255, 0.7);
                    padding: 0.25rem 0.5rem;
                    border-radius: 12px;
                    font-size: 0.75rem;
                }

                .patient-preview-data {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.5rem;
                }

                .data-type-group {
                    display: flex;
                    align-items: flex-start;
                    flex-wrap: wrap;
                    gap: 0.25rem;
                }

                .file-list {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.25rem;
                }

                .file-item {
                    background: rgba(255, 255, 255, 0.05);
                    color: rgba(255, 255, 255, 0.6);
                    padding: 0.2rem 0.5rem;
                    border-radius: 4px;
                    font-size: 0.75rem;
                    max-width: 200px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .upload-options {
                    background: rgba(255, 255, 255, 0.05);
                    padding: 1rem;
                    border-radius: 8px;
                }

                .preview-actions {
                    display: flex;
                    justify-content: flex-end;
                    padding-top: 1rem;
                    border-top: 1px solid rgba(255, 255, 255, 0.1);
                }
            `}</style>
        </div>
    );
};

export default DatasetFolderUpload;
